import {
  Client,
  GeneratedFile,
  Model,
  ModelInstance,
  RowIdentifier,
  Scenario,
  Timescale,
  User,
  UserDisplayInfo,
} from 'src/types/models';

export enum PortfolioReportTypes {
  Portfolio = 'Portfolio',
  // StandardReport = 'Standard Report',
  Waterfall = 'Waterfall',
  // DASHBOARD = 'Dashboard',
  DriverAnalysis = 'Driver Analysis',
  Tornado = 'Tornado',
}

/**
 * Report types configured against a single model instance with scenario A/B chart metadata (as opposed to
 * Portfolio's data cuts). Tornado shares the Waterfall configuration and metadata shape.
 */
export const isChartConfiguredReportType = (
  type: PortfolioReportTypes | string | undefined
): boolean =>
  type === PortfolioReportTypes.Waterfall ||
  type === PortfolioReportTypes.DriverAnalysis ||
  type === PortfolioReportTypes.Tornado;

export interface PortfolioReportMemberships {
  Role: PortfolioReportMembershipRole;
  UserID: User['id'];
  LastRunAt: string | null;
}

export interface PortfolioReportData {
  /**
   * Unique Id of the dashboard created in BI solution
   */
  ClientID: Client['id'];
  /**
   * Report description
   */
  Description: string | null;
  Metadata: null | any;
  ModelID: Model['id'];
  ModelInstanceID: ModelInstance['id'];
  /**
   * Name of the report
   */
  Name: string;
  PermissionLevel: number;
  PortfolioDataCuts: PortfolioReportDatacut[];
  ReportMemberships: PortfolioReportMemberships[];
  /**
   * Indicates if a report is available to the broader viewer group or just the owner group
   * 0: Edit
   * 1: Published
   */
  State: 0 | 1;
  /**
   * Enumerator denoting the report type.
   * Excel
   * Dashboard
   */
  Type: PortfolioReportTypes;
  /**
   * Unique Report Id
   */
  id: number;
  deleted_at: string | null;
  deleted_by: User['id'] | null;
  /**
   * Last time when dashboard/report was refreshed/edited
   */
  updated_at: string;
  /**
   * User id of user who updated the report last
   */
  updated_by: UserDisplayInfo;
  updated_by_id: User['id'];
  ReportTemplateName: string;
  ReportTemplateURL: string;
  CustomDownloadBlobName: string;
  Finalized?: PortfolioReportDataFinalizedFlag;
  Model?: { Name: string };
  ModelInstance?: { Name: string };
}

export enum PortfolioReportDataFinalizedFlag {
  NotFinalized = 0,
  Finalized = 1,
}

interface PortfolioReportDatacutMetaDataDimensions {
  dimension: RowIdentifier['DimNumber'];
  dimensionInstances: RowIdentifier['Name'][];
  sourceDimension: number;
}

interface PortfolioReportDatacutMetaData {
  CutType: 'inputs' | 'outputs';
  ModelInstance: ModelInstance['id'];
  ModelInstanceLock: 0 | 1;
  NumScenarios: number;
  RangeType: 'max' | 'equal';
  ScenarioLock: 0 | 1;
  Timescales?: Timescale[];
  Scenarios: Array<Scenario['id']>;
  Outputs?: Array<RowIdentifier['Name']>;
  Inputs?: Array<RowIdentifier['Name']>;
  Dimensions: PortfolioReportDatacutMetaDataDimensions[];
}

export interface PortfolioReportDatacut {
  DataSourceID: number;
  ModelID: Model['id'];
  Name: string;
  id: number;
  updated_at: string;
  updated_by_id: User['id'];
  MetaData: PortfolioReportDatacutMetaData;
}

export enum PortfolioReportMembershipRole {
  Viewer = 0,
  Owner = 1,
  Restricted = 2,
}

export enum PortfolioReportPermissions {
  Private = 0,
  Shared = 1,
}

export interface PortfolioReportMembership {
  ReportID: PortfolioReportData['id'];
  Role: PortfolioReportMembershipRole;
  UserID: User['id'];
  id: number;
}

export interface PortfolioReportGeneratedFile extends GeneratedFile {
  ReportID: PortfolioReportData['id'];
}
